<template>
  <div id="about-page" class="container">
    <div v-if="!voteSubmitted" class="row mt-4">
      <div class="col-10 mx-auto">
        
        <!-- Header -->
        <div class="row mt-4 mb-4 vote-page-header">
          <div class="col-12 desktop-heading-m">
            VOTE Comfort NYC 
          </div>
        </div>
        
        <!-- Display the coordinate below the header (desktop only; hidden on mobile to reduce DOM) -->
        <div v-if="!mobileFlag" class="row mb-4">
          <div class="col-12">
            <p v-if="coordinate && coordinate.length">
              Coordinates: {{ coordinate[1].toFixed(4) }}, {{ coordinate[0].toFixed(4) }}
            </p>
            <p v-else>
              Coordinates: None
            </p>
          </div>
        </div>
        

        <!-- Weather: full overlay on desktop; minimal one-line on mobile to reduce DOM -->
        <template v-if="!mobileFlag">
          <div class="weather-overlay" v-if="weatherData">
            <div class="weather-layout">
              <div class="weather-col weather-col-icon">
                <div id="weather-icon">{{ weatherIcon }}</div>
                <div class="weather-description">{{ weatherData.weather[0].description }}</div>
              </div>
              <div class="weather-col weather-col-center">
                <div class="info-item">
                  <span class="label">Temperature</span>
                  <span class="value">{{ (weatherData.main.temp).toFixed(1) }}°C</span>
                </div>
                <div class="info-item">
                  <span class="label">Humidity</span>
                  <span class="value">{{ weatherData.main.humidity }}%</span>
                </div>
              </div>
              <div class="weather-col weather-col-right">
                <div class="info-item">
                  <span class="label">Feels Like</span>
                  <span class="value">{{ (weatherData.main.feels_like).toFixed(1) }}°C</span>
                </div>
                <div class="info-item">
                  <span class="label">Wind</span>
                  <span class="value">{{ (weatherData.wind.speed).toFixed(1) }} m/s</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="coordinate && coordinate.length" class="weather-overlay weather-loading">
            <p>Loading weather data...</p>
          </div>
          <div v-else class="weather-overlay weather-prompt">
            <p>Weather data will appear when coordinates are selected</p>
          </div>
        </template>
        <div v-else class="weather-overlay weather-mobile-minimal">
          <p v-if="weatherData">{{ weatherIcon }} {{ (weatherData.main.temp).toFixed(1) }}°C</p>
          <p v-else-if="coordinate && coordinate.length">Loading weather…</p>
          <p v-else>Tap a spot on the map to see weather</p>
        </div>

        <!-- Location status for vote gating -->
        <div v-if="locationStatus === 'idle'" class="location-message location-idle">
          <p>To vote we need to confirm you're near Bryant Park.</p>
          <button type="button" class="use-location-btn" @click="requestLocation">Use my location</button>
        </div>
        <div v-else-if="locationStatus === 'loading'" class="location-message location-loading">
          <p>Checking your location…</p>
        </div>
        <div v-else-if="locationStatus === 'denied' || locationStatus === 'unavailable' || locationStatus === 'timeout'" class="location-message location-error">
          <p>Location is required to vote. Please enable location access and try again.</p>
        </div>
        <div v-else-if="locationStatus === 'outOfBounds'" class="location-message location-out-of-bounds">
          <p>You must be near Bryant Park to submit a vote.</p>
        </div>
        
        <div>
          <p>
            <span v-if="!mobileFlag">Email:</span>
            <input type="text" id="nameInput" placeholder="Enter your email" v-model="userName">
          </p>
        </div>
        
        <!-- Vote buttons -->
        <div class="vote-buttons">
          <div class="comfort-slider-container">
            <label for="comfort-slider">How do you feel?</label>
            <input 
              type="range" 
              id="comfort-slider" 
              v-model="comfortValue" 
              min="1" 
              max="5" 
              step="1"
              :disabled="!canVote"
              class="comfort-slider"
            >
            <div class="slider-labels slider-labels-5">
              <span>Too<br>Cold</span>
              <span>Cold</span>
              <span>Comfortable</span>
              <span>Hot</span>
              <span>Too<br>Hot</span>
            </div>
          </div>
          <div class="comfort-slider-container">
            <label for="clothing-slider">What is your "Clothing level"?</label>
            <input 
              type="range" 
              id="clothing-slider" 
              v-model="clothingValue" 
              min="1" 
              max="3" 
              step="1"
              :disabled="!canVote"
              class="comfort-slider"
            >
            <div class="slider-labels slider-labels-3">
              <span>Heavy Coat</span>
              <span>Light Jacket</span>
              <span>Shirt</span>
            </div>
          </div>
          <div class="comfort-slider-container">
            <label for="activity-slider">What is your "Activity level"?</label>
            <input 
              type="range" 
              id="activity-slider" 
              v-model="activityValue" 
              min="1" 
              max="4" 
              step="1"
              :disabled="!canVote"
              class="comfort-slider"
            >
            <div class="slider-labels slider-labels-4">
              <span>Sitting</span>
              <span>Standing</span>
              <span>Walk</span>
              <span>Speedwalk</span>
            </div>
          </div>
          <button @click="submitVote" class="submit-vote-btn" :disabled="!canVote">Submit Vote</button>
        </div>

        <!-- Error display -->
        <div v-if="errorMessage" class="error-message mt-3">
          {{ errorMessage }}
        </div>
        
        <!-- Vote confirmation -->
        <div v-if="voteSubmitted" class="vote-confirmation mt-3">
          Thank you for your vote! Your comfort assessment has been recorded.
        </div>
      </div>
    </div>
    <thank-you 
      v-else 
      :vote-data="lastVoteData"
      @submit-another="resetForm"
      @return-to-map="returnToMap"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ThankYou from './ThankYou.vue'
import { WEATHER_ICONS } from '@/assets/constants/weatherIcons';
import { isNightInNYC } from '@/utils/nycTime';
import { isInsideParkBounds } from '@/utils/parkLocation';

export default {
  name: "VotePage",
  components: {
    ThankYou
  },
  props: {
    coordinate: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      weatherData: null,
      weatherIcon: "🌡️",
      userName: "",
      errorMessage: "",
      voteSubmitted: false,
      comfortValue: 3, // Default to "Comfortable"
      clothingValue: 2, // Default to "Light jacket"
      activityValue: 1, // Default to "Sitting"
      lastVoteData: null,
      comfortLabels: {
        1: "tooCold",
        2: "cold",
        3: "comfortable",
        4: "hot",
        5: "tooHot"
      },
      weatherIcons: WEATHER_ICONS,
      deviceLocation: null,
      locationStatus: 'idle', // idle | loading | allowed | denied | unavailable | timeout | outOfBounds
      weatherFetchTimer: null
    }
  },
  computed: {
    ...mapGetters({
      mobileFlag: "getMobileFlag"
    }),
    isNearPark() {
      return this.locationStatus === 'allowed';
    },
    canVote() {
      return this.coordinate && this.coordinate.length && this.userName.trim() !== "" && this.weatherData && this.isNearPark;
    }
  },
  mounted() {
    // Location is requested only when user clicks "Use my location" to avoid iOS Safari/Chrome reload/crash on permission prompt.
  },
  watch: {
    coordinate: {
      handler(newCoord) {
        if (this.weatherFetchTimer) {
          clearTimeout(this.weatherFetchTimer);
          this.weatherFetchTimer = null;
        }
        if (newCoord && newCoord.length === 2) {
          this.voteSubmitted = false;
          this.errorMessage = "";
          // Defer fetch to next tick + short delay so panel can paint first (reduces iOS reload risk)
          this.$nextTick(() => {
            this.weatherFetchTimer = setTimeout(() => {
              this.fetchWeatherData(newCoord[1], newCoord[0]);
              this.weatherFetchTimer = null;
            }, 500);
          });
        } else {
          this.weatherData = null;
          this.weatherIcon = "🌡️";
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    if (this.weatherFetchTimer) {
      clearTimeout(this.weatherFetchTimer);
      this.weatherFetchTimer = null;
    }
  },
  methods: {
    requestLocation() {
      if (!navigator.geolocation) {
        this.locationStatus = 'unavailable';
        return;
      }
      this.locationStatus = 'loading';
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          this.deviceLocation = { lat, lng };
          this.locationStatus = isInsideParkBounds(lat, lng) ? 'allowed' : 'outOfBounds';
        },
        (error) => {
          this.deviceLocation = null;
          if (error.code === 1) this.locationStatus = 'denied';
          else if (error.code === 2) this.locationStatus = 'unavailable';
          else if (error.code === 3) this.locationStatus = 'timeout';
          else this.locationStatus = 'unavailable';
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    },
    async fetchWeatherData(lat, lon) {
      try {
        this.weatherData = null;
        this.errorMessage = "";
        
        // OpenWeatherMap API - replace with your API key
        const apiKey = "a2748b3e1a6a440e20c54471956f4e99"; 
        const url = `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&units=metric&appid=${apiKey}`;
        
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error("Weather data unavailable");
        }
        
        const data = await response.json();
        this.weatherData = data;
        
        // Set weather icon based on description (use night icon for clear sky 6pm–6am NYC)
        const weatherDescription = data.weather[0].description.toLowerCase();
        const iconKey =
          weatherDescription === 'clear sky' && isNightInNYC()
            ? 'clear sky (night)'
            : weatherDescription;
        if (this.weatherIcons[iconKey]) {
          this.weatherIcon = this.weatherIcons[iconKey];
        } else {
          this.weatherIcon = this.weatherIcons['default'];
        }
      } catch (error) {
        console.error("Error fetching weather data:", error);
        this.errorMessage = "Error fetching weather data. Please try again later.";
      }
    },
    
    async submitVote() {
      if (!this.canVote) {
        this.errorMessage = "Please select coordinates and enter your name before voting.";
        return;
      }
      
      // Create payload with all relevant data
      const payload = {
        user_email: this.userName,
        comfort_vote_result: this.comfortValue,
        clothing_level: this.clothingValue,
        activity_level: this.activityValue,
        weather_condition: this.weatherData.weather[0].description,
        coordinate: {
          x: this.coordinate[1], // latitude
          y: this.coordinate[0]  // longitude
        },
        device_lat: this.deviceLocation ? this.deviceLocation.lat : null,
        device_lng: this.deviceLocation ? this.deviceLocation.lng : null
      };
      
      try {
        // Store the vote data
        await this.$store.dispatch('storeComfortVote', payload);
        this.lastVoteData = payload;
        
        // Emit event to store the marker
        this.$emit('vote-submitted');
        
        // Show success message
        this.voteSubmitted = true;
        this.errorMessage = "";
      } catch (error) {
        this.errorMessage = "Error submitting vote. Please try again.";
        console.error("Error submitting vote:", error);
      }
    },

    resetForm() {
      this.voteSubmitted = false;
      this.userName = "";
      this.comfortValue = 3;
      this.clothingValue = 2;
      this.activityValue = 1;
      this.errorMessage = "";
    },

    returnToMap() {
      this.$emit('return-to-map');
    }
  }
}
</script>

<style lang="scss" scoped>
#aboutPage {
  p {
    text-align: justify;
  }
}

.background {
  background-color: rgb(26, 26, 26);
}

.spacer-sm {
  height: 2rem;
}

.spacer-md {
  height: 4rem;
}

/* Weather overlay styling */
.weather-overlay {
  padding: 20px;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
  margin-bottom: 20px;
}

.location-message {
  padding: 12px 20px;
  border-radius: 8px;
  margin-bottom: 16px;
  color: white;
}

.location-message p {
  margin: 0;
}

.location-idle {
  background-color: rgba(0, 0, 0, 0.4);
}

.location-loading {
  background-color: rgba(0, 0, 0, 0.4);
}

.use-location-btn {
  margin-top: 10px;
  padding: 6px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.9rem;
}

.use-location-btn:hover {
  background-color: #0056b3;
}

.location-error {
  background-color: rgba(180, 60, 60, 0.6);
}

.location-out-of-bounds {
  background-color: rgba(180, 100, 60, 0.6);
}

/* Weather: mobile = stacked; tablet/desktop = 3 columns */
.weather-layout {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

@media (min-width: 768px) {
  .weather-layout {
    flex-direction: row;
    justify-content: space-around;
    align-items: stretch;
    gap: 20px;
  }

  .weather-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 10px;
  }

  .weather-col-icon {
    align-items: center;
    text-align: center;
  }

  .weather-col-center,
  .weather-col-right {
    justify-content: center;
  }
}

.weather-col-icon {
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

#weather-icon {
  font-size: 60px;
  margin: 10px 0;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.8); /* Add shadow for better visibility */
}

.weather-description {
  font-style: italic;
  text-transform: capitalize;
  margin-top: 5px;
  font-size: 1.2rem;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.8);
}

/* Mobile: smaller header, tighter spacing, 2-column grid for weather if shown */
@media (max-width: 767px) {
  .vote-page-header {
    margin-top: 0.5rem !important;
    margin-bottom: 0.5rem !important;
  }

  .vote-page-header .desktop-heading-m {
    font-size: 1rem;
    font-weight: normal;
  }

  .weather-overlay {
    display: none;
  }

  .weather-mobile-minimal {
    display: block;
    margin-top: 0.25rem;
  }

  .weather-col-center,
  .weather-col-right {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
    width: 100%;
    max-width: 100%;
  }

  .weather-col-center .info-item,
  .weather-col-right .info-item {
    margin: 0;
  }
}

.info-item {
  text-align: center;
}

.label {
  font-weight: bold;
  display: block;
  margin-bottom: 5px;
  font-size: 0.9rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.value {
  font-size: 1.2rem;
  font-weight: 500;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.vote-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.comfort-slider-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 6px;
  margin-bottom: 6px;
  position: relative;
  
  label {
    font-size: 1.2rem;
    margin-bottom: 4px;
    color: white;
  }
}

.comfort-slider {
  width: 80%;
  height: 22px;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  margin: 5px 0;
  
  &:focus {
    outline: none;
  }
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ccc;
    cursor: pointer;
    margin-top: -7px;
  }
  
  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 4px;
    background: #ddd;
    border-radius: 4px;
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ccc;
    cursor: pointer;
    border: none;
  }

  &::-moz-range-track {
    height: 4px;
    background: #ddd;
    border-radius: 4px;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &::-webkit-slider-thumb {
      background: #ccc;
      cursor: not-allowed;
    }
  }
}

.slider-labels {
  display: flex;
  width: 80%;
  margin-top: 4px;
  position: relative;
  min-height: 2.2em;
  
  span {
    font-size: 0.9rem;
    color: white;
    text-align: center;
    position: absolute;
    transform: translateX(-50%);
    
    &:nth-child(1) { left: 0%; }
    &:nth-child(2) { left: 25%; }
    &:nth-child(3) { left: 50%; }
    &:nth-child(4) { left: 75%; }
    &:nth-child(5) { left: 100%; }
  }
}

.slider-labels-3 span {
  &:nth-child(1) { left: 0%; }
  &:nth-child(2) { left: 50%; }
  &:nth-child(3) { left: 100%; }
}

.slider-labels-4 span {
  &:nth-child(1) { left: 0%; }
  &:nth-child(2) { left: 33.33%; }
  &:nth-child(3) { left: 66.66%; }
  &:nth-child(4) { left: 100%; }
}

.submit-vote-btn {
  margin-top: 10px;
  margin-bottom:60px;
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s;
  align-self: center;
  
  &:hover:not(:disabled) {
    background-color: #0056b3;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
}

.error-message {
  color: #f44336;
  font-weight: bold;
}

.vote-confirmation {
  color: #4CAF50;
  font-weight: bold;
}

.weather-loading, .weather-prompt {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-style: italic;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.weather-mobile-minimal {
  padding: 12px 20px;
  text-align: center;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.weather-mobile-minimal p {
  margin: 0;
  font-size: 1rem;
}

input[type="text"] {
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  margin-left: 8px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
}

/* Desktop: fixed width; mobile: full width (override after global so it wins) */
@media (min-width: 768px) {
  input[type="text"] {
    width: 250px;
  }
}

@media (max-width: 767px) {
  input[type="text"] {
    width: 100%;
    max-width: 100%;
    margin-left: 0;
  }
}
</style>